import type { RealtimeChannel } from 'ably';
import { Realtime } from 'ably';
import { useCallback } from 'react';

import { useAblyAuthUrl } from 'hb-react/shared/hooks/useSubscribeToAblyChannel/hooks/useAblyAuthUrl';
import type { AblyTokenType } from 'hb-react/shared/hooks/useSubscribeToAblyChannel/types';
import { clusterToEnv } from 'hb-react/shared/hooks/useSubscribeToAblyChannel/utils/clusterToEnv';

let realtime: Realtime | undefined;
let activeTokenType: AblyTokenType | undefined;

// DO NOT USE OUTSIDE OF TESTING
export const testOnlyResetLocalState = () => {
  realtime = undefined;
  activeTokenType = undefined;
};

interface UseAblyRealtimeProps {
  tokenType: AblyTokenType;
}

export const useAblyRealtime = (
  { tokenType }: UseAblyRealtimeProps = {
    tokenType: 'frontoffice',
  },
) => {
  // Auth URL will only have a value if the flag is enabled and
  // all the required contexts are available
  const authUrl = useAblyAuthUrl(tokenType);
  const isAblyEnabled = !!authUrl;

  const getAblyRealtime = useCallback(async () => {
    if (!isAblyEnabled) {
      throw new Error('Ably is not enabled');
    }

    if (!realtime) {
      // Using a global variable to store the Ably client for now to ensure we share a single
      // connection across the app even when multiple components using portals are mounted.
      realtime = new Realtime({
        authUrl,
        environment: clusterToEnv(window.__HB_ENV__.CLUSTER_NAME),
        logLevel: 1,
        autoConnect: false, // We will manually connect the client to avoid unecessary connections
      });
      activeTokenType = tokenType;
    }

    // Safety net to ensure every ably subscribers are using the same token type
    // on the page.
    if (activeTokenType !== tokenType) {
      throw new Error(
        `Ably tokenType mismatch. Realtime has been created with: ${activeTokenType} but you are trying to use ${tokenType}.`,
      );
    }

    // See https://ably.com/docs/connect/states
    switch (realtime.connection.state) {
      case 'connected':
        break;
      case 'initialized':
      case 'suspended':
      case 'closed':
      case 'failed':
        realtime.connection.connect();
        await realtime.connection.once('connected');
        break;
      // From https://ably.com/docs/connect/states: In the disconnected state an SDK will periodically attempt to open
      // a new connection (approximately every 15 seconds), anticipating the connection will be re-established soon
      // and connection and channel continuity will be possible.
      case 'disconnected':
      case 'connecting':
        await realtime.connection.once('connected');
        break;
      case 'closing':
        await realtime.connection.once('closed');
        realtime.connection.connect();
        await realtime.connection.once('connected');
        break;
      default:
        throw new Error('Unknown connection state');
    }

    return realtime;
  }, [authUrl, isAblyEnabled, tokenType]);

  const closeIfUnused = useCallback(() => {
    if (!realtime) {
      return;
    }

    // For now we only cover the happy path. If the connection is in
    // any other state than connected we will not try to force close it.
    if (realtime.connection.state !== 'connected') {
      return;
    }

    let inUse = false;

    // @ts-expect-error Ably types are not up-to-date
    Object.values<RealtimeChannel>(realtime.channels.all).forEach((channel) => {
      // @ts-expect-error Ably types are not up-to-date
      inUse ||= channel.subscriptions.any.length > 0;
    });

    if (!inUse) {
      try {
        realtime.close();
      } catch {
        // Given the asynchronous nature of closing the connection and the fact that
        // we have multiple useSubscribeToAblyChannel hooks in the app, it is unfortunately
        // common for race conditions to happen and close to throw an error.
        // We can safely ignore the error.
      } finally {
        realtime = undefined;
      }
    }
  }, []);

  return { getAblyRealtime, isAblyEnabled, closeIfUnused };
};
